import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  finishHostedParentClaim,
  loadHostedAccount,
  remoteClient,
} from "../data/remote";
import {
  SIGN_UP_PASSWORD_MIN,
  updateParentPhoneMetadata,
} from "../domain/supabaseAuth";
import {
  AUTH_STORAGE_KEY,
  DEMO_PASSWORD,
  applyDemoAccounts,
  attachStudentToParent as attachStudentToParentRecord,
  destinationForAccount,
  emptyAuthState,
  ensureStudentAccount as ensureStudentAccountRecord,
  ensureStudentAccounts as ensureStudentAccountsRecord,
  findAccount,
  normalizeAccount,
  normalizeEmail,
  requestPasswordReset as requestPasswordResetRecord,
  resetPassword as resetPasswordRecord,
  rememberSignedInParent,
  signIn as signInRecord,
  signOut as signOutRecord,
  signUp as signUpRecord,
  type AuthAccount,
  type AuthSession,
  type AuthState,
  type SignUpInput,
} from "../domain/auth";
import { staff, type Student } from "../domain/data";
import {
  finishParentClaim as finishParentClaimRecord,
  issueMigratedParents,
  validateParentClaim,
  type ParentClaimInput,
  type ParentInvite,
} from "../domain/parentClaim";
import {
  devShortcutParentPhone,
  isLocalDevShortcutEmail,
} from "../domain/devShortcuts";
import { localDevShortcutsEnabled, runtimeFromBrowser } from "../domain/env";
import { beginWait } from "./loaderActivity";
import { useStudents } from "./students";

type AuthActionResult =
  | { ok: true; account: AuthAccount }
  | { ok: false; error: string };

type ResetRequestResult =
  | { ok: true; token: string; email: string; channel: "local" | "supabase" }
  | { ok: false; error: string };

type AuthContextValue = {
  loading: boolean;
  session: AuthSession | null;
  account: AuthAccount | null;
  accounts: AuthAccount[];
  signIn: (email: string, password: string) => Promise<AuthActionResult>;
  signUp: (input: SignUpInput) => Promise<AuthActionResult>;
  adoptHostedSession: () => Promise<AuthActionResult>;
  continueSignedInParent: (input: {
    given: string;
    family: string;
    email: string;
  }) => AuthActionResult;
  signOut: () => void;
  requestPasswordReset: (email: string) => Promise<ResetRequestResult>;
  resetPassword: (
    token: string,
    password: string,
    confirmPassword: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  ensureStudentAccount: (student: Student) => void;
  ensureStudentAccounts: (students: Student[]) => void;
  attachStudentToParent: (studentId: string) => void;
  linkMigratedParents: (students: Student[]) => {
    invites: ParentInvite[];
    skipped: { email: string; reason: string }[];
  };
  finishParentClaim: (
    input: ParentClaimInput,
  ) => Promise<{ ok: true; account: AuthAccount } | { ok: false; error: string }>;
  pathForAccount: (account: AuthAccount) => string;
  configureDemoAccounts: (enabled: boolean) => void;
  saveParentPhone: (
    phone: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function persist(state: AuthState) {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

function demosEnabledNow(): boolean {
  if (typeof window === "undefined") return false;
  return localDevShortcutsEnabled(
    runtimeFromBrowser().name,
    window.location.search,
  );
}

function readStore(students: Student[]): AuthState {
  const seeded = emptyAuthState(students, staff);
  let stored = seeded;
  if (typeof window !== "undefined") {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as AuthState;
        if (parsed && Array.isArray(parsed.accounts)) {
          stored = {
            accounts: parsed.accounts.map(normalizeAccount),
            session: parsed.session ?? null,
            resets: Array.isArray(parsed.resets) ? parsed.resets : [],
          };
        }
      }
    } catch {
      stored = seeded;
    }
  }
  return applyDemoAccounts(stored, students, demosEnabledNow(), staff);
}

function wait(): Promise<void> {
  const end = beginWait();
  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve();
      // Let the caller start its next request before this wait lets the veil close.
      queueMicrotask(end);
    }, 420);
  });
}

function accountWithKnownPhone(account: AuthAccount | null): AuthAccount | null {
  if (!account) return null;
  if (account.phone?.trim()) return account;
  if (account.role !== "parent" || !demosEnabledNow()) return account;
  const phone = devShortcutParentPhone(account.email);
  return phone ? { ...account, phone } : account;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { students } = useStudents();
  const demosRef = useRef(demosEnabledNow());
  const [store, setStore] = useState<AuthState>(() => readStore(students));
  const [hosted, setHosted] = useState<AuthAccount | null>(null);
  const [loading, setLoading] = useState<boolean>(() => Boolean(remoteClient()));

  const write = useCallback((next: AuthState) => {
    setStore(next);
    persist(next);
  }, []);

  const adoptHostedSession = useCallback(async (): Promise<AuthActionResult> => {
    const client = remoteClient();
    if (!client) return { ok: false, error: "Sign-in is not configured." };
    try {
      const loaded = await loadHostedAccount(client);
      const account = accountWithKnownPhone(loaded);
      if (!account) return { ok: false, error: "Could not open that account." };
      setHosted(account);
      setStore((current) => {
        const next = signOutRecord(current);
        persist(next);
        return next;
      });
      return { ok: true, account };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Could not open that account.";
      return { ok: false, error: message };
    }
  }, []);

  useEffect(() => {
    const client = remoteClient();
    if (!client) {
      setLoading(false);
      return;
    }
    let active = true;
    void client.auth.getSession().then(async ({ data: { session } }) => {
      if (!active) return;
      if (!session) {
        setHosted(null);
        setLoading(false);
        return;
      }
      try {
        const account = await loadHostedAccount(client);
        if (active) setHosted(account ? accountWithKnownPhone(account) : null);
      } catch {
        if (active) setHosted(null);
      } finally {
        if (active) setLoading(false);
      }
    });

    const { data } = client.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setHosted(null);
        setLoading(false);
        return;
      }
      void loadHostedAccount(client)
        .then((account) =>
          setHosted(account ? accountWithKnownPhone(account) : null),
        )
        .catch(() => setHosted(null))
        .finally(() => setLoading(false));
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setHosted((current) => {
      if (!current || current.role !== "parent") return current;
      if (
        students.length === 0 &&
        (current.studentIds.length > 0 || current.pendingStudentIds.length > 0)
      ) {
        return current;
      }
      const accepted = students
        .filter((student) => (student.acceptance ?? "accepted") === "accepted")
        .map((student) => student.id);
      const pending = students
        .filter((student) => student.acceptance === "pending")
        .map((student) => student.id);
      if (
        accepted.length === current.studentIds.length &&
        accepted.every((id, index) => id === current.studentIds[index]) &&
        pending.length === current.pendingStudentIds.length &&
        pending.every((id, index) => id === current.pendingStudentIds[index])
      ) {
        return current;
      }
      return { ...current, studentIds: accepted, pendingStudentIds: pending };
    });
  }, [students]);

  useEffect(() => {
    setStore((current) => {
      const next = applyDemoAccounts(current, students, demosRef.current, staff);
      if (next === current) return current;
      persist(next);
      return next;
    });
  }, [students]);

  const configureDemoAccounts = useCallback(
    (enabled: boolean) => {
      demosRef.current = enabled;
      setStore((current) => {
        const next = applyDemoAccounts(current, students, enabled, staff);
        if (next === current) return current;
        persist(next);
        return next;
      });
    },
    [students],
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<AuthActionResult> => {
      await wait();
      const result = signInRecord(store, email, password);
      if (!result.ok) return { ok: false, error: result.error };
      write(result.state);
      const account = findAccount(result.state, email);
      if (!account) return { ok: false, error: "No account for that email." };
      return { ok: true, account };
    },
    [store, write],
  );

  const signUp = useCallback(
    async (input: SignUpInput): Promise<AuthActionResult> => {
      await wait();
      const result = signUpRecord(store, input);
      if (!result.ok) return { ok: false, error: result.error };
      write(result.state);
      const account = findAccount(result.state, input.email);
      if (!account) return { ok: false, error: "Could not create that account." };
      return { ok: true, account };
    },
    [store, write],
  );

  const continueSignedInParent = useCallback(
    (input: { given: string; family: string; email: string }): AuthActionResult => {
      const result = rememberSignedInParent(store, input);
      if (!result.ok) return { ok: false, error: result.error };
      write(result.state);
      const account = findAccount(result.state, input.email);
      if (!account) return { ok: false, error: "Could not open that account." };
      return { ok: true, account };
    },
    [store, write],
  );

  const signOut = useCallback(() => {
    const client = remoteClient();
    if (client) void client.auth.signOut();
    setHosted(null);
    write(signOutRecord(store));
  }, [store, write]);

  const requestPasswordReset = useCallback(
    async (email: string): Promise<ResetRequestResult> => {
      await wait();
      const client = remoteClient();
      const demo =
        demosRef.current &&
        (isLocalDevShortcutEmail(email) ||
          findAccount(store, email)?.password === DEMO_PASSWORD);
      if (client && !demo) {
        const { error } = await client.auth.resetPasswordForEmail(normalizeEmail(email), {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) return { ok: false, error: error.message };
        return {
          ok: true,
          token: "",
          email: normalizeEmail(email),
          channel: "supabase",
        };
      }
      const result = requestPasswordResetRecord(store, email);
      if (!result.ok) {
        return { ok: false, error: result.error };
      }
      write(result.state);
      return {
        ok: true,
        token: result.token,
        email: normalizeEmail(email),
        channel: "local",
      };
    },
    [store, write],
  );

  const resetPassword = useCallback(
    async (
      token: string,
      password: string,
      confirmPassword: string,
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      await wait();
      const client = remoteClient();
      if (client && !token) {
        if (password !== confirmPassword) {
          return { ok: false, error: "Passwords do not match." };
        }
        if (password.length < SIGN_UP_PASSWORD_MIN) {
          return {
            ok: false,
            error: `Password needs at least ${SIGN_UP_PASSWORD_MIN} characters.`,
          };
        }
        const { data, error: sessionError } = await client.auth.getSession();
        if (sessionError || !data.session) {
          return { ok: false, error: "This reset link expired. Request a new one." };
        }
        const { error } = await client.auth.updateUser({ password });
        if (error) return { ok: false, error: error.message };
        return { ok: true };
      }
      const result = resetPasswordRecord(
        store,
        token,
        password,
        confirmPassword,
      );
      if (!result.ok) return { ok: false, error: result.error };
      write(result.state);
      return { ok: true };
    },
    [store, write],
  );

  const ensureStudentAccount = useCallback(
    (student: Student) => {
      const next = ensureStudentAccountRecord(store, student);
      if (next !== store) write(next);
    },
    [store, write],
  );

  const ensureStudentAccounts = useCallback(
    (studentsToSave: Student[]) => {
      const next = ensureStudentAccountsRecord(store, studentsToSave);
      if (next !== store) write(next);
    },
    [store, write],
  );

  const linkMigratedParents = useCallback(
    (imported: Student[]) => {
      if (remoteClient()) {
        return { invites: [], skipped: [] };
      }
      const result = issueMigratedParents(store, imported);
      write(result.state);
      return { invites: result.invites, skipped: result.skipped };
    },
    [store, write],
  );

  const finishParentClaim = useCallback(
    async (
      input: ParentClaimInput,
    ): Promise<{ ok: true; account: AuthAccount } | { ok: false; error: string }> => {
      const current = hosted ?? (store.session ? findAccount(store, store.session.email) : null);
      if (!current || current.role !== "parent") {
        return { ok: false, error: "Sign in with the parent account from the invitation." };
      }
      const client = remoteClient();
      if (hosted && client) {
        const error = validateParentClaim(current, input, SIGN_UP_PASSWORD_MIN);
        if (error) return { ok: false, error };
        const saved = await finishHostedParentClaim(client, {
          given: input.given,
          family: input.family,
          phone: input.phone,
          password: input.password,
          mustResetPassword: current.mustResetPassword,
          decisions: input.decisions,
        });
        if (!saved.ok) return saved;
        return adoptHostedSession();
      }
      const result = finishParentClaimRecord(
        store,
        current.email,
        input,
      );
      if (!result.ok) return { ok: false, error: result.error };
      write(result.state);
      const account = findAccount(result.state, current.email);
      if (!account) return { ok: false, error: "Could not save that account." };
      return { ok: true, account };
    },
    [adoptHostedSession, hosted, store, write],
  );

  const attachStudentToParent = useCallback(
    (studentId: string) => {
      if (!store.session) return;
      const next = attachStudentToParentRecord(
        store,
        store.session.email,
        studentId,
      );
      if (next !== store) write(next);
    },
    [store, write],
  );

  const saveParentPhone = useCallback(
    async (
      phone: string,
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      const trimmed = phone.trim();
      if (!trimmed) {
        return { ok: false, error: "Add a mobile number for guardian contact." };
      }
      const client = remoteClient();
      if (hosted && client) {
        const result = await updateParentPhoneMetadata(client, trimmed);
        if (!result.ok) return result;
        setHosted({ ...hosted, phone: trimmed });
        return { ok: true };
      }
      if (store.session) {
        const email = store.session.email;
        const accounts = store.accounts.map((item) =>
          item.email === email ? { ...item, phone: trimmed } : item,
        );
        write({ ...store, accounts });
        return { ok: true };
      }
      return { ok: false, error: "Sign in to save guardian contact." };
    },
    [hosted, store, write],
  );

  const account =
    hosted ??
    (store.session ? (findAccount(store, store.session.email) ?? null) : null);
  const session = hosted
    ? { email: hosted.email, role: hosted.role }
    : store.session;

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      session,
      account,
      accounts: store.accounts,
      signIn,
      signUp,
      adoptHostedSession,
      continueSignedInParent,
      signOut,
      requestPasswordReset,
      resetPassword,
      ensureStudentAccount,
      ensureStudentAccounts,
      attachStudentToParent,
      linkMigratedParents,
      finishParentClaim,
      pathForAccount: destinationForAccount,
      configureDemoAccounts,
      saveParentPhone,
    }),
    [
      account,
      adoptHostedSession,
      attachStudentToParent,
      finishParentClaim,
      linkMigratedParents,
      configureDemoAccounts,
      ensureStudentAccount,
      ensureStudentAccounts,
      saveParentPhone,
      requestPasswordReset,
      resetPassword,
      continueSignedInParent,
      signIn,
      signOut,
      signUp,
      loading,
      session,
      store.accounts,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
